// Every helper now requires an explicit `lang` parameter. Callers in
// React components pass `i18n.language` from useTranslation(); callers
// in non-component contexts import the i18n singleton and read
// `i18n.language` directly. See doc/DECISIONS.md "Locale-Aware
// Formatters" for the design rationale (Quebec convention "29,50 $"
// vs en-CA "$29.50", 24h vs 12h time, and why the backend export
// pipeline is intentionally untouched).
//
// Normalize on the way in so callers can pass any of 'en', 'fr',
// 'en-CA', 'fr-CA' without ambiguity. Anything starting with 'fr'
// becomes fr-CA; everything else becomes en-CA. Add more branches
// here if/when additional locales ship.
function normalizeLocale(lang: string): string {
  return lang.startsWith('fr') ? 'fr-CA' : 'en-CA';
}

export function formatCurrency(amount: number, currency: string, lang: string): string {
  return new Intl.NumberFormat(normalizeLocale(lang), {
    style: 'currency',
    currency,
  }).format(amount);
}

export function formatDate(dateStr: string, timezone: string, lang: string): string {
  return new Intl.DateTimeFormat(normalizeLocale(lang), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  }).format(new Date(dateStr));
}

export function formatTimeLocal(timeLocal: string, lang: string): string {
  // Parse "HH:mm" and synthesize a Date so Intl.DateTimeFormat can produce
  // locale-correct output (12h "2:30 PM" in en-CA, 24h "14 h 30" in fr-CA).
  // The synthesized Date is a vehicle for the formatter; only hour and
  // minute round-trip.
  const [hours, minutes] = timeLocal.split(':').map(Number);
  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return new Intl.DateTimeFormat(normalizeLocale(lang), {
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}

export function formatDateShort(dateStr: string, lang: string): string {
  return new Intl.DateTimeFormat(normalizeLocale(lang), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

export function formatLocalDate(localDateTime: string, lang: string): string {
  const date = new Date(localDateTime);
  return new Intl.DateTimeFormat(normalizeLocale(lang), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatLocalTime(localDateTime: string, lang: string): string {
  // Locale-aware: 12h for en-CA, 24h for fr-CA. No explicit hour12
  // override -- Intl picks the convention for the locale.
  const date = new Date(localDateTime);
  return new Intl.DateTimeFormat(normalizeLocale(lang), {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function formatDateTime(dateStr: string, lang: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat(normalizeLocale(lang), {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}
